import EventSourceStream from "./utils/eventSourceStream";
import APP_CONFIG from "../app/configuration/AppConfig";
import {
  AgentMessageUnhandledError,
  IConversationHistory,
  IConversationRecord,
  IUserPrompt,
} from "./chatModels";
import { getConfiguredAuthHeaders } from "../auth/authUtils";

export async function postMessage(
  prompt: IUserPrompt,
  onMessage: (message: string) => void,
  abortSignal?: AbortSignal
): Promise<void> {
  try {
    const url = `${getUrlPrefix()}/conversation`;
    const authHeaders = await getConfiguredAuthHeaders();
    const res = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "text/event-stream",
        ...authHeaders,
      },
      body: JSON.stringify(prompt),
      signal: abortSignal,
    });
    const decoder = new EventSourceStream();
    res.body?.pipeThrough(decoder);
    const reader = decoder.readable.getReader();
    while (!abortSignal?.aborted) {
      const { value, done } = await reader.read();
      if (done) {
        return;
      }
      onMessage(value.data);
    }
  } catch (error) {
    if (!abortSignal?.aborted) {
      console.error("Error during conversation:", error);
      onMessage(
        JSON.stringify({
          type: "unhandled_error",
          message: "An error occurred while processing your request.",
        } as AgentMessageUnhandledError)
      );
    }
  }
}

export async function getConversationsList(signal?: AbortSignal): Promise<IConversationRecord[]> {
  const url = `${getUrlPrefix()}/conversation/list`;
  const authHeaders = await getConfiguredAuthHeaders();
  const res = await fetch(url, {
    method: "GET",
    signal,
    headers: {
      Accept: "application/json",
      ...authHeaders,
    },
  });
  if (!res.ok) {
    throw new Error(`Failed to fetch conversations list: ${res.statusText}`);
  }
  const data: Array<
    Omit<IConversationRecord, "createdAt" | "updatedAt"> & { createdAt: string; updatedAt: string }
  > = await res.json();
  return (
    data.map((conversation) => ({
      id: conversation.id,
      subject: conversation.subject,
      createdAt: Date.parse(conversation.createdAt),
      updatedAt: Date.parse(conversation.updatedAt),
    })) || []
  );
}

export async function getConversationHistory(
  conversationId: string,
  signal?: AbortSignal
): Promise<IConversationHistory> {
  const url = `${getUrlPrefix()}/conversation/history/${conversationId}`;
  const authHeaders = await getConfiguredAuthHeaders();
  const res = await fetch(url, {
    method: "GET",
    signal,
    headers: {
      Accept: "application/json",
      ...authHeaders,
    },
  });
  if (!res.ok) {
    throw new Error(`Failed to fetch conversation history: ${res.statusText}`);
  }
  return await res.json();
}

export async function deleteConversation(conversationId: string, signal?: AbortSignal): Promise<void> {
  const url = `${getUrlPrefix()}/conversation/${conversationId}`;
  const authHeaders = await getConfiguredAuthHeaders();
  const res = await fetch(url, {
    method: "DELETE",
    signal,
    headers: {
      Accept: "application/json",
      ...authHeaders,
    },
  });
  if (!res.ok) {
    throw new Error(`Failed to delete conversation: ${res.statusText}`);
  }
}

function getUrlPrefix() {
  return APP_CONFIG.serviceUrls.aiAgentHostUrl.endsWith("/")
    ? APP_CONFIG.serviceUrls.aiAgentHostUrl.slice(0, -1)
    : APP_CONFIG.serviceUrls.aiAgentHostUrl;
}
